import * as React from "react";
import { AddFormGroupSidePanel, FormGroupNodeOverview, PageNodeOverview, TemplateNode, TemplateNodeTypeEnum } from "@app/access-reports/admin/template-update";
import { getFormGroupByIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import { cloneDeep } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { AccessReportsWizardFormGroup } from "@app/access-reports/shared/accessReportsWizardFormGroup";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";

interface NodeOverviewProps {
  selectedNode: TemplateNode;
  selectedNodeType: TemplateNodeTypeEnum;
  setSelectedNode: React.Dispatch<React.SetStateAction<TemplateNode>>;
  setTemplate: React.Dispatch<React.SetStateAction<AccessReportsWizardTemplate>>;
  template: AccessReportsWizardTemplate;
}

export const NodeOverview: React.FC<React.PropsWithChildren<NodeOverviewProps>> = (props: NodeOverviewProps) => {
  const [modalVisible, setModalVisible] = React.useState<boolean>(false);
  const [sidePanelVisible, setSidePanelVisible] = React.useState<boolean>(false);
  const deleteButtonRef = React.useRef<HTMLButtonElement>(null);

  const nodeOverview: JSX.Element = React.useMemo(() => {
    switch (props.selectedNodeType) {
      case TemplateNodeTypeEnum.Page:
        const page: AccessReportsWizardPage = getPageFromTemplate(props.template, props.selectedNode.nodeId as AccessReportsWizardStepEnum);

        return (
          <PageNodeOverview
            page={page}
          />
        );
      case TemplateNodeTypeEnum.FormGroup:
        const formGroup: AccessReportsWizardFormGroup = getFormGroupByIdFromTemplate(props.template, props.selectedNode.parentNodeTitle as AccessReportsWizardStepEnum, parseInt(props.selectedNode.nodeId));

        return (
          <FormGroupNodeOverview
            formGroup={formGroup}
          />
        );
      default:
        break;
    }
  }, [props.selectedNode, props.template]);

  const nodeChild: TemplateNodeTypeEnum = React.useMemo(() => {
    switch (props.selectedNodeType) {
      case TemplateNodeTypeEnum.Page:
        return TemplateNodeTypeEnum.FormGroup;
      case TemplateNodeTypeEnum.FormGroup:
        return TemplateNodeTypeEnum.Element;
      //case TemplateNodeTypeEnum.Element:
      //  return TemplateNodeTypeEnum.Option;
      //case TemplateNodeTypeEnum.Option:
      //  return null;
      default:
        break;
    }
  }, [props.selectedNodeType]);

  const getModal = (): JSX.Element => {
    return (
      <Modal
        closeModal={() => setModalVisible(false)}
        content={{
          body: <>Are you sure you want to delete the {props.selectedNodeType.toLowerCase()}: <b>{props.selectedNode.displayName}</b>? <br /><br />
            You will not be able to undo this action unless you leave the template node editor, thus abandoning any changes you may have made.</>,
          cancelButtonText: "Cancel",
          confirmButtonAction: () => onDeleteNodeConfirmClick(),
          confirmButtonText: "Delete",
          confirmButtonType: "primary-danger",
          title: "Warning",
        }}
        isVisible={modalVisible}
        hideBodyScroll={true}
        triggerElement={deleteButtonRef}
      />
    );
  }

  const getSidePanelContent = (): JSX.Element => {
    return (
      <AddFormGroupSidePanel
        onAddNodeClick={onAddNodeClick}
        page={props.selectedNode.nodeId as AccessReportsWizardStepEnum}
        template={props.template}
      />
    );
  }

  const hideSidePanel = (): void => {
    if (sidePanelVisible) {
      setSidePanelVisible(false);
    }
  }

  const onDeleteNodeConfirmClick = (): void => {
    const template: AccessReportsWizardTemplate = cloneDeep(props.template);

    switch (props.selectedNodeType) {
      case TemplateNodeTypeEnum.Page:
        template.pages = template.pages.filter(x => x.title !== props.selectedNode.nodeId);
        break;
      case TemplateNodeTypeEnum.FormGroup:
        const page: AccessReportsWizardPage = getPageFromTemplate(template, props.selectedNode.parentNodeTitle as AccessReportsWizardStepEnum);

        if (!!page) {
          page.formGroups = page.formGroups.filter(x => x.id.toString() !== props.selectedNode.nodeId);
        }

        break;
      default:
        break;
    }

    props.setSelectedNode(null);
    props.setTemplate(template);
  }

  const onAddNodeClick = (template: AccessReportsWizardTemplate): void => {
    props.setTemplate(template);
    setSidePanelVisible(false);
  }

  return (
    <React.Fragment>
      <div className="row">
        {modalVisible &&
          getModal()
        }

        <div className="col-8">
          {nodeOverview}
        </div>

        <div className="col-4">
          <div className="float-end">
            {nodeChild && (
              <FlyoutLink
                classNames="qes-button-secondary"
                content={{
                  body: getSidePanelContent(),
                  title: "Add Form Group",
                }}
                disabled={props.selectedNodeType === TemplateNodeTypeEnum.FormGroup}
                panelId="access-reports-admin__template-update--add-node"
                isVisible={sidePanelVisible}
                size="20"
                testId="access-reports-admin__template-update--add-node-link"
                onClose={hideSidePanel}
              >
                Add {nodeChild}
              </FlyoutLink>
            )}

            <span className="mx-3">
              |
            </span>

            <QESButton
              onClick={() => setModalVisible(true)}
              qesTestId="access-reports-admin__template-update--delete-node"
              qesType="secondary-danger"
              qesRef={deleteButtonRef}
            >
              Delete {props.selectedNodeType}
            </QESButton>
          </div>

        </div>
      </div>
    </React.Fragment>
  );
}
